'use client';

import type { ReactNode } from 'react';

type SettingsWorkingHoursRowProps = {
  /**
   * Leading control — the day's checkbox. Bounded to a fixed width so every
   * row's time fields line up in one column however long the day name is.
   */
  lead?: ReactNode;
  /** Start-of-range control. */
  from: ReactNode;
  /** End-of-range control. */
  to: ReactNode;
  /** Trailing control after a "for" separator — the break window's duration. */
  duration?: ReactNode;
  className?: string;
};

type SettingsWorkingHoursStackProps = {
  children: ReactNode;
  className?: string;
};

/**
 * The prototype's own inline measurements for this row (`.vrow`): a 150px lead
 * column, 130px time fields and a 120px duration select. The design system has
 * no field-width token, so they live here as named constants — the same
 * rationale as `SettingsField`'s width set.
 */
const LEAD_WIDTH = 'flex h-[2.25rem] w-[9.375rem] shrink-0 items-center';
// The native clock icon inside a time input is hidden (Figma draws none, parity #381); the
// picker still opens from the keyboard.
const TIME_WIDTH = 'w-[8.125rem] shrink-0 [&_input::-webkit-calendar-picker-indicator]:hidden';
const DURATION_WIDTH = 'w-[7.5rem] shrink-0';
// The separators and the lead are as tall as the 36px time field and centre their text in it,
// so "to" and "for" sit on the field's middle whatever the row's own height is (parity #296).
// At the fixed-column breakpoint the row is exactly one 36px field tall (Figma: day rows 44 apart, 36 + 8),
// whatever the native time input or checkbox label would otherwise add; below it the row may wrap.
const SEPARATOR = 'typography-body-sm flex h-[2.25rem] shrink-0 items-center text-text-secondary';

/**
 * One time-range line inside a settings row's control column — the prototype's
 * `.vrow`: an optional leading control, a from/to pair either side of a "to"
 * separator, and optionally a duration after a "for" separator.
 *
 * It is a layout only. The controls are the caller's, which is what lets the
 * same row serve both the working-hours days (checkbox + from/to) and the break
 * window (from/to + duration). Time fields are native `type="time"` inputs at
 * every call site today; the package's `TimePicker` (0.33.3) changes the keys
 * and the half-typed value, so that swap waits on a decision. That is a
 * call-site choice, not something this row assumes.
 */
export const SettingsWorkingHoursRow = ({
  lead,
  from,
  to,
  duration,
  className,
}: SettingsWorkingHoursRowProps) => (
  <div
    data-lula-ds=""
    className={`flex flex-wrap items-start gap-space-8 font-sans min-[82.5rem]:h-[2.25rem] min-[82.5rem]:flex-nowrap ${
      className ? ` ${className}` : ''
    }`}
  >
    {lead && <div className={LEAD_WIDTH}>{lead}</div>}
    <div className={TIME_WIDTH}>{from}</div>
    <span className={SEPARATOR}>to</span>
    <div className={TIME_WIDTH}>{to}</div>
    {duration && (
      <>
        <span className={SEPARATOR}>for</span>
        <div className={DURATION_WIDTH}>{duration}</div>
      </>
    )}
  </div>
);

/**
 * Stacks `SettingsWorkingHoursRow`s vertically — the prototype's `.vstack`.
 *
 * A settings row's control column lays its children out in a wrapping flex ROW,
 * so a run of day rows needs its own column container to read as a list.
 */
export const SettingsWorkingHoursStack = ({
  children,
  className,
}: SettingsWorkingHoursStackProps) => (
  <div
    data-lula-ds=""
    className={`flex flex-col gap-space-8 font-sans${className ? ` ${className}` : ''}`}
  >
    {children}
  </div>
);

export default SettingsWorkingHoursRow;
